import React, {
	useMemo, useState, useRef, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ColorResult } from '@uiw/react-color';
import {
	getChannelLogoFromMeeting,
	getFullAssetURI,
} from '../../../utils/utils';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import { UploadImage, UploadResponse } from '../../../connection/upload-image';
import WaitingIndicator from '../../general-components/waiting-indicator';
import ImagePicker from './image-picker';
import { GreenroomState } from '../../../stores/types/types';
import { AppSettings, DefaultMeetingImage } from '../../../stores/reducers/settings';
import { GetUserMediaCanvasWallpapers, UploadUserMediaCanvasWallpaper, DeleteUserMedia } from '../../../connection/media';
import { UserMedia, ImagePickerMedia } from '../../../stores/reducers/media';
import InfoBadge, { InfoBadgeMsg } from '../../general-components/tooltips/info-badge';
import { IMG_TYPES } from '../../../types/constants';
import { getFileExtension, resizeImage } from '../../../utils/media';
import ColorPicker from './color-pickers/color-picker';

interface Props {
	settingsForShotEdited: (newQueue: any) => void;
	updateDefaultMeetingImage: (wallpaperData: DefaultMeetingImage) => void;
	appliedMatchesQueue: boolean;
	queue: AppSettings;
}

interface FullColor {
	hex: string
}

export default function WallpaperBg(props: Props) {
	const {
		meeting,
		user,
		token,
	} = useSelector((state: GreenroomState) => state.SessionReducer);

	const { queue } = props;

	const [uploading, setUploading] = useState(false);
	const fileInput = useRef<HTMLInputElement | null>(null);
	const [fullColor, setFullColor] = useState<FullColor>({ hex: queue.wallpaperColor });
	const [showColorPicker, setShowColorPicker] = useState(false);
	const [showBackgroundImagePicker, setShowBackgroundImagePicker] = useState(false);
	const [wallpapers, setWallpapers] = useState<UserMedia[]>([]);
	const [scale, setScale] = useState(queue.wallpaperScale);
	const scaleDebounce = useRef<NodeJS.Timeout | null>(null);
	const colorDebounce = useRef<NodeJS.Timeout | null>(null);

	function getWallpapers() {
		if (!token || !user || !meeting) {
			return;
		}

		GetUserMediaCanvasWallpapers(token, meeting.meeting).then((response: UserMedia[]) => {
			setWallpapers(response);
		});
	}

	useEffect(() => {
		getWallpapers();
	}, []);

	useEffect(() => {
		getWallpapers();
	}, [user, token, meeting]);

	useEffect(() => {
		if (props.appliedMatchesQueue) {
			setFullColor({ hex: queue.wallpaperColor });
			setScale(queue.wallpaperScale);
		}
	}, [props.appliedMatchesQueue]);

	function closeColorPicker() {
		setShowColorPicker(false);
	}

	function toggleColorPicker() {
		setShowColorPicker(!showColorPicker);
	}

	function closeBackgroundImagePicker() {
		setShowBackgroundImagePicker(false);
	}

	function changeWallpaperImage(url: string) {
		const newQueue = { ...queue, wallpaperType: 'image', wallpaperImage: url };
		props.settingsForShotEdited(newQueue);
		props.updateDefaultMeetingImage({
			image: url,
			color: queue.wallpaperColor,
			scale: queue.wallpaperScale,
		});
	}

	async function checkFileInput(event: React.ChangeEvent<HTMLInputElement>) {
		if (!token || !user || !meeting) {
			return;
		}

		const newImage = event.target.files?.[0];
		if (!newImage) return;
		
		const extension = getFileExtension(newImage?.name ?? '');
		const resizedImg = await resizeImage(newImage, extension, 1920, 1080);
		if (!resizedImg) return;

		setUploading(true);

		const url = await UploadImage(token, resizedImg)
			.then((res: UploadResponse) => res.url)
			.catch(() => '');
			
		setUploading(false);
		
		if (!url) return;

		changeWallpaperImage(url);

		await UploadUserMediaCanvasWallpaper(token, user.gr_user, meeting.meeting, url);

		getWallpapers();
	}

	function clickFileInput() {
		fileInput.current?.click();
	}

	const wallpaperStyle = useMemo(() => {
		const style: any = {
			backgroundColor: '#000',
			backgroundPosition: 'center',
		};

		if (queue.wallpaperImage) {
			style.backgroundImage = `url("${getFullAssetURI(queue.wallpaperImage)}")`;
			style.backgroundSize = `${scale}%`;
			style.backgroundColor = fullColor.hex;
		}

		return style;
	}, [fullColor.hex, queue.wallpaperImage, scale]);

	function handleScrub(e: React.ChangeEvent<HTMLInputElement>): void {
		const newScale = Number(e.target.value);
		setScale(newScale);

		if (scaleDebounce.current) { clearTimeout(scaleDebounce.current); }

		scaleDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, wallpaperScale: newScale };
			props.settingsForShotEdited(newQueue);
			props.updateDefaultMeetingImage({
				image: queue.wallpaperImage,
				color: queue.wallpaperColor,
				scale: newScale,
			});
		}, 100);
	}

	function clearWallpaperImage() {
		const newQueue = { ...queue, wallpaperType: 'color' };
		props.settingsForShotEdited(newQueue);
		props.updateDefaultMeetingImage({
			image: '',
			color: queue.wallpaperColor,
			scale: queue.wallpaperScale,
		});
	}

	function colorChange(color: ColorResult) {
		setFullColor(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, wallpaperColor: color.hex };
			props.settingsForShotEdited(newQueue);
			props.updateDefaultMeetingImage({
				image: queue.wallpaperImage,
				color: color.hex,
				scale: queue.wallpaperScale,
			});
		}, 100);
	}

	function deleteWallpaper(image: ImagePickerMedia) {
		if (!token) {
			return;
		}

		DeleteUserMedia(token, image.id).then(getWallpapers);
	}

	async function openImagePicker() {
		try {
			await getWallpapers();
		} finally {
			setShowBackgroundImagePicker(true);
		}
	}

	const channelLogo: string = useMemo(() => getChannelLogoFromMeeting(meeting), [meeting]);

	return (
		<div className="settings-container wallpaper-container">
			<label className="section-title margin-bottom-medium">
				<span className={classNames('title-text')}>Wallpaper</span>
			</label>
			<div className="background-fill-options">
				<div className="background-image-container">
					{queue.wallpaperType === 'image'
						? (<div className="background-image" style={wallpaperStyle} onClick={openImagePicker} />)
						: (<div className="color-background" style={{ backgroundColor: fullColor.hex }} onClick={() => setShowBackgroundImagePicker(true)}>Color Only</div>)}
				</div>
				<div className="background-image-select">
					<div className="display-flex width-full margin-bottom-24">
						<span className="text-dark-gray margin-right-small">Wallpaper Fill</span>
						<InfoBadge message={InfoBadgeMsg.Wallpaper} />
					</div>
					<button className="image-select round padding-small background-lighter text-white width-full" onClick={clickFileInput} disabled={uploading}>
						{uploading ? <WaitingIndicator /> : <><Icon name={ICONS.ADD} size={14} color={COLORS.WHITE} className='margin-right-small' /><span>Add</span></>}
					</button>
				</div>
			</div>
			<input
				accept={IMG_TYPES
					.map((a) => `.${a}`)
					.join(',')
				}
				onChange={checkFileInput}
				ref={fileInput}
				type="file"
				style={{ display: 'none' }}
			/>

			<ImagePicker
				selectedImage={queue.wallpaperImage}
				backgroundType={queue.wallpaperType}
				logo={channelLogo}
				thumbnail={meeting?.thumbnail ?? ''}
				label={'Wallpaper Image'}
				close={closeBackgroundImagePicker}
				open={showBackgroundImagePicker}
				setImage={changeWallpaperImage}
				clearBgImage={clearWallpaperImage}
				externalImages={wallpapers}
				deleteImage={deleteWallpaper}
			/>

			<ColorPicker
				open={showColorPicker}
				openHexAtStart={fullColor.hex}
				portalId='color-portal-wallpaper'
				onClose={() => setShowColorPicker(false)}
				onChange={colorChange}
				customPosition={{top:22,right:-1}}
				pickerClassname='color-picker-darker'
			/>

			<div className={`background-scale ${queue.wallpaperType === 'image' ? '' : 'disabled'}`}>
				<label>Scale</label>
				<input
					onChange={handleScrub}
					type="range"
					min="20"
					max="250"
					value={scale}
					disabled={queue.wallpaperType !== 'image'}
				/>
				<span>{scale}%</span>
			</div>

			<div className="background-color-container">
				<label>Background color</label>
				<div className={classNames('color-toggle', { editing: showColorPicker })} onClick={toggleColorPicker}>
					<div className="color-swatch" style={{ backgroundColor: fullColor.hex }}></div>
					<span className='format-hex'>{fullColor.hex}</span>
				</div>
				<div id='color-portal-wallpaper' className='color-portal portal-right-aligned'/>
			</div>
		</div>
	);
}
